import React, { useState, useEffect } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { listUserGroups } from "../graphql/queries";
import { updateUser } from "../graphql/mutations";

/**
 * UserGroupDropdown component for assigning users to groups
 *
 * @param {Object} props - Component props
 * @param {string} props.userId - The ID of the user to update
 * @param {string} props.currentGroups - The current group ID(s) assigned to the user
 * @param {Function} props.onGroupUpdate - Callback when group is updated
 * @param {Function} props.onUserUpdated - Callback to reload user data
 * @param {string} props.currentUserID - The ID of the current logged-in user
 */
const UserGroupDropdown = ({
  userId,
  currentGroups,
  onGroupUpdate,
  onUserUpdated,
  currentUserID,
}) => {
  const [selectedGroup, setSelectedGroup] = useState("");
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentGroups) {
      const groupId = currentGroups;
      if (groupId) {
        setSelectedGroup(groupId);
      } else {
        setSelectedGroup("");
      }
    } else {
      setSelectedGroup("");
    }
  }, [currentGroups, availableGroups]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const result = await API.graphql(
          graphqlOperation(listUserGroups, {
            filter: { userID: { eq: currentUserID } },
          })
        );

        const groups = result.data.listUserGroups?.items || [];
        const groupsWithNames = groups.map((g) => ({
          id: g.id,
          name: g.title,
        }));
        const sortedGroups = groupsWithNames.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setAvailableGroups(sortedGroups);
      } catch (error) {
        console.error("Error fetching user groups:", error);
        setAvailableGroups([]);
      }
    };

    if (currentUserID) {
      fetchGroups();
    }
  }, [currentUserID]);

  const handleChange = async (event) => {
    const newGroupId = event.target.value;
    setLoading(true);

    try {
      if (newGroupId) {
        const updateResult = await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userId,
              userGroupID: newGroupId,
            },
          })
        );

        const selectedGroupObject = availableGroups.find(
          (g) => g.id === newGroupId
        );
        setSelectedGroup(newGroupId);
        onGroupUpdate(
          userId,
          selectedGroupObject ? [selectedGroupObject.name] : []
        );
      } else {
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userId,
              userGroupID: null,
            },
          })
        );

        setSelectedGroup("");
        onGroupUpdate(userId, []);
      }

      if (onUserUpdated) {
        onUserUpdated();
      }
    } catch (error) {
      console.error("Error updating user group:", error);
      console.log("Error details:", JSON.stringify(error, null, 2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormControl fullWidth size="small">
      <Select
        value={selectedGroup}
        onChange={handleChange}
        displayEmpty
        disabled={loading || availableGroups.length === 0}
      >
        <MenuItem value="">
          <em>Unassigned</em>
        </MenuItem>
        {availableGroups.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default UserGroupDropdown;
